import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { 
    Trophy, 
    Clock, 
    Eye,
    X,
    Check,
    ChevronLeft,
    Sparkles,
} from 'lucide-react';

interface Difference {
    id: number;
    x: number;
    y: number;
    found: boolean;
    hint: string;
}

interface Level {
    id: number;
    name: string;
    differences: Difference[];
}

// 游戏关卡配置
const CLICK_TOLERANCE = 6; // 点击容差百分比，越小越精确

const levels: Level[] = [
    {
        id: 1,
        name: '简约风景',
        differences: [
            { id: 1, x: 27, y: 20, found: false, hint: '太阳' },
            { id: 2, x: 70, y: 33, found: false, hint: '云朵' },
            { id: 3, x: 15, y: 46, found: false, hint: '树木' },
            { id: 4, x: 60, y: 68, found: false, hint: '烟囱' },
            { id: 5, x: 86, y: 55, found: false, hint: '花朵' },
        ],
    },
    {
        id: 2,
        name: '城市夜景',
        differences: [
            { id: 1, x: 20, y: 25, found: false, hint: '月亮' },
            { id: 2, x: 40, y: 40, found: false, hint: '窗户' },
            { id: 3, x: 80, y: 30, found: false, hint: '星星' },
            { id: 4, x: 35, y: 93, found: false, hint: '汽车' },
            { id: 5, x: 10, y: 83, found: false, hint: '路灯' },
        ],
    },
    {
        id: 3,
        name: '森林探险',
        differences: [
            { id: 1, x: 30, y: 28, found: false, hint: '蝴蝶' },
            { id: 2, x: 65, y: 24, found: false, hint: '鸟巢' },
            { id: 3, x: 50, y: 55, found: false, hint: '蘑菇' },
            { id: 4, x: 15, y: 75, found: false, hint: '松鼠' },
            { id: 5, x: 85, y: 45, found: false, hint: '浆果' },
        ],
    },
];

const Game: React.FC = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [differences, setDifferences] = useState<Difference[]>([]);
    const [timeLeft, setTimeLeft] = useState(90);
    const [gameState, setGameState] = useState<'menu' | 'playing' | 'won' | 'lost'>('menu');
    const [score, setScore] = useState(0);
    const [totalScore, setTotalScore] = useState(0);
    const [hintUsed, setHintUsed] = useState(false);
    const [clickFeedback, setClickFeedback] = useState<{ x: number; y: number; correct: boolean } | null>(null);

    const level = levels[currentLevel];

    const startGame = useCallback(() => {
        setDifferences(level.differences.map(d => ({ ...d, found: false })));
        setTimeLeft(120);
        setGameState('playing');
        setScore(0);
        setHintUsed(false);
    }, [level]);

    const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (gameState !== 'playing') return;

        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        // 检查是否点击了不同之处
        const clickedDiff = differences.find(
            (d) => !d.found && Math.abs(d.x - x) < CLICK_TOLERANCE && Math.abs(d.y - y) < CLICK_TOLERANCE
        );

        if (clickedDiff) {
            // 找对了
            setDifferences(prev => prev.map(d => 
                d.id === clickedDiff.id ? { ...d, found: true } : d
            ));
            setScore(prev => prev + (hintUsed ? 50 : 100));
            showFeedback(x, y, true);
        } else {
            // 找错了
            showFeedback(x, y, false);
        }
    };

    const showFeedback = (x: number, y: number, correct: boolean) => {
        setClickFeedback({ x, y, correct });
        setTimeout(() => setClickFeedback(null), 500);
    };

    const useHint = () => {
        const unfound = differences.filter(d => !d.found);
        if (unfound.length > 0 && !hintUsed) {
            const hint = unfound[0];
            setDifferences(prev => prev.map(d => 
                d.id === hint.id ? { ...d, x: d.x, y: d.y } : d
            ));
            setHintUsed(true);
            setScore(prev => Math.max(0, prev - 50));
        }
    };

    const nextLevel = () => {
        if (currentLevel < levels.length - 1) {
            const nextLevelIndex = currentLevel + 1;
            setCurrentLevel(nextLevelIndex);
            // 初始化新关卡
            setDifferences(levels[nextLevelIndex].differences.map(d => ({ ...d, found: false })));
            setTimeLeft(120);
            setGameState('playing');
            setScore(0);
            setHintUsed(false);
        } else {
            setGameState('menu');
            setCurrentLevel(0);
            setTotalScore(0);
        }
    };

    // 检查是否获胜
    useEffect(() => {
        if (gameState === 'playing' && differences.length > 0 && differences.every(d => d.found)) {
            setTotalScore(prev => prev + score);
            setGameState('won');
        }
    }, [differences, gameState, score]);

    // 计时器
    useEffect(() => {
        if (gameState !== 'playing') return;
        
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    setGameState('lost');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [gameState]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // 渲染场景（使用SVG绘制简单图形）
    const renderScene = (isModified: boolean) => {
        const scenes = [
            // 场景1：简约风景
            <svg key="scene1" viewBox="0 0 100 100" className="w-full h-full">
                {/* 天空 */}
                <rect x="0" y="0" width="100" height="100" fill="#87CEEB" />
                {/* 太阳 - 不同之处1：位置微调 */}
                <circle cx={isModified ? "28" : "27"} cy="20" r="7" fill="#FFD700" />
                {/* 云朵 - 不同之处2：略小 */}
                <ellipse cx="70" cy="33" rx={isModified ? "10" : "11"} ry={isModified ? "5" : "5.5"} fill="white" />
                {/* 草地 */}
                <rect x="0" y="60" width="100" height="40" fill="#90EE90" />
                {/* 树木 - 不同之处3：微调位置 */}
                <g>
                    <rect x={isModified ? "13" : "15"} y="45" width="4" height="15" fill="#8B4513" />
                    <circle cx={isModified ? "15" : "17"} cy="42" r="5" fill="#228B22" />
                </g>
                {/* 房屋 - 不同之处4：烟囱有无 */}
                <g>
                    <rect x="55" y="65" width="15" height="12" fill="#FF6B6B" />
                    <polygon points="55,65 62.5,58 70,65" fill="#8B0000" />
                    <rect x="60" y="70" width="4" height="7" fill="#8B4513" />
                    {!isModified && <rect x="58" y="57" width="3" height="5" fill="#8B4513" />}
                    {isModified && <rect x="58" y="67" width="3" height="3" fill="#87CEEB" />}
                    {!isModified && <rect x="58" y="67" width="3" height="3" fill="#87CEEB" />}
                </g>
                {/* 花朵 - 不同之处5：颜色微调 */}
                <g>
                    <circle cx="85" cy="55" r="2.5" fill={isModified ? "#FF69B4" : "#FF1493"} />
                    <circle cx="88" cy="57" r="2" fill={isModified ? "#FFB6C1" : "#FF69B4"} />
                </g>
                {/* 小路 */}
                <path d="M 45 100 Q 50 80 55 100" fill="#DEB887" />
            </svg>,
            // 场景2：城市夜景
            <svg key="scene2" viewBox="0 0 100 100" className="w-full h-full">
                {/* 夜空 */}
                <rect x="0" y="0" width="100" height="100" fill="#1a1a2e" />
                {/* 月亮 - 不同之处1：位置微调 */}
                <circle cx={isModified ? "21" : "20"} cy="25" r="5" fill="#F0E68C" />
                {/* 星星 - 不同之处3：闪烁效果 */}
                <polygon points="80,30 81,32 83,32 81.5,33 82,35 80,34 78,35 78.5,33 77,32 79,32" fill={isModified ? "#FFF" : "#FFD700"} />
                {/* 建筑物 */}
                <rect x="10" y="40" width="20" height="60" fill="#2d2d44" />
                <rect x="35" y="30" width="25" height="70" fill="#3d3d5c" />
                <rect x="65" y="50" width="25" height="50" fill="#2d2d44" />
                {/* 窗户 - 不同之处2：位置微调 */}
                <rect x={isModified ? "39" : "40"} y="40" width="4" height="4" fill="#FFFF00" />
                <rect x="15" y="50" width="3" height="3" fill="#87CEEB" />
                <rect x="22" y="50" width="3" height="3" fill="#87CEEB" />
                <rect x="15" y="58" width="3" height="3" fill="#87CEEB" />
                <rect x="22" y="58" width="3" height="3" fill="#87CEEB" />
                <rect x="70" y="60" width="4" height="4" fill="#FFFF00" />
                <rect x="80" y="60" width="4" height="4" fill="#FFFF00" />
                {/* 汽车 - 不同之处4：位置微调 */}
                <rect x={isModified ? "34" : "35"} y="92" width="8" height="3" fill="#FF0000" rx="1" />
                {/* 路灯 - 不同之处5：亮度 */}
                <g>
                    <rect x="10" y="80" width="1" height="8" fill="#666" />
                    <circle cx="10.5" cy="79" r="1.5" fill={isModified ? "#FFFACD" : "#FFFF00"} />
                </g>
                {/* 地面 */}
                <rect x="0" y="95" width="100" height="5" fill="#333" />
            </svg>,
            // 场景3：森林探险
            <svg key="scene3" viewBox="0 0 100 100" className="w-full h-full">
                {/* 天空 */}
                <rect x="0" y="0" width="100" height="100" fill="#98D8C8" />
                {/* 树木背景 */}
                <rect x="5" y="20" width="15" height="80" fill="#8B4513" />
                <polygon points="12.5,0 0,30 25,30" fill="#228B22" />
                <rect x="80" y="30" width="12" height="70" fill="#8B4513" />
                <polygon points="86,10 75,40 97,40" fill="#228B22" />
                {/* 蝴蝶 - 不同之处1：翅膀大小 */}
                <g>
                    <ellipse cx="30" cy="28" rx={isModified ? "2.5" : "3"} ry={isModified ? "1.5" : "2"} fill="#FF69B4" />
                    <ellipse cx="26" cy="29" rx="2" ry="3" fill="#FF1493" />
                    <ellipse cx="34" cy="29" rx="2" ry="3" fill="#FF1493" />
                </g>
                {/* 鸟巢 - 不同之处2：鸟蛋数量 */}
                <g>
                    <ellipse cx="65" cy="24" rx="5" ry="3" fill="#DEB887" />
                    {!isModified && <><circle cx="63" cy="23" r="1.2" fill="#87CEEB" /><circle cx="67" cy="23" r="1.2" fill="#87CEEB" /></>}
                    {isModified && <circle cx="65" cy="23" r="1.5" fill="#87CEEB" />}
                </g>
                {/* 草地 */}
                <rect x="0" y="70" width="100" height="30" fill="#7CFC00" />
                {/* 蘑菇 - 不同之处3：斑点位置 */}
                <g>
                    <rect x="48" y="60" width="4" height="10" fill="#F5DEB3" />
                    <ellipse cx="50" cy="58" rx="5" ry="4" fill="#FF0000" />
                    {!isModified && <circle cx="48" cy="57" r="1" fill="white" />}
                    {isModified && <circle cx="52" cy="56" r="1" fill="white" />}
                </g>
                {/* 松鼠 - 不同之处4：尾巴方向 */}
                <g>
                    <ellipse cx="15" cy="80" rx="4" ry="3" fill="#D2691E" />
                    <circle cx="13" cy="78" r="2.5" fill="#D2691E" />
                    <circle cx="16" cy="77" r="1" fill="black" />
                    <path d={isModified ? "M 18 75 Q 22 72 20 70" : "M 8 75 Q 5 72 7 70"} stroke="#D2691E" strokeWidth="1.5" fill="none" />
                </g>
                {/* 浆果 - 不同之处5：数量 */}
                <g>
                    <circle cx="85" cy="45" r="2" fill="#8B0000" />
                    {!isModified && <><circle cx="87" cy="47" r="1.5" fill="#8B0000" /><circle cx="83" cy="46" r="1.5" fill="#8B0000" /></>}
                    {isModified && <circle cx="88" cy="44" r="1.5" fill="#8B0000" />}
                </g>
                {/* 小路 */}
                <path d="M 40 100 Q 50 85 60 100" fill="#DEB887" />
            </svg>,
        ];
        return scenes[currentLevel];
    };

    // 菜单界面
    if (gameState === 'menu') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-indigo-900 py-8 px-4">
                <SEO title="找不同游戏" description="挑战你的观察力，找出两张图片的不同之处！" />
                <div className="max-w-lg mx-auto">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg mb-4">
                            <Sparkles className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-2">找不同</h1>
                        <p className="text-gray-600 dark:text-gray-300">找出两张图片中的不同之处</p>
                    </div>

                    {totalScore > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 shadow-lg text-center">
                            <p className="text-gray-600 dark:text-gray-300">上次得分</p>
                            <p className="text-3xl font-bold text-blue-600">{totalScore}</p>
                        </div>
                    )}

                    <button
                        onClick={startGame}
                        className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                    >
                        开始游戏
                    </button>

                    <div className="mt-8 space-y-4">
                        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 text-center">游戏说明</h2>
                        <div className="bg-white/50 dark:bg-gray-800/50 rounded-xl p-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                            <p>🖱️ 点击图片中不同的地方</p>
                            <p>⏱️ 每关限时 90 秒</p>
                            <p>💡 正确点击 +100 分，使用提示 +50 分</p>
                            <p>🔍 共 3 个关卡等你挑战</p>
                        </div>
                    </div>

                    <Link
                        to="/"
                        className="flex items-center justify-center gap-2 mt-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        返回首页
                    </Link>
                </div>
            </div>
        );
    }

    // 获胜界面
    if (gameState === 'won') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 dark:from-gray-900 dark:to-green-900 py-8 px-4">
                <SEO title="恭喜过关！" description="你成功通过了这一关！" />
                <div className="max-w-lg mx-auto text-center">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full shadow-lg mb-6 animate-bounce">
                        <Trophy className="w-12 h-12 text-white" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-2">
                        {currentLevel < levels.length - 1 ? '太棒了！' : '全部通关！'}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                        {currentLevel < levels.length - 1 ? '即将进入下一关' : '你完成了所有关卡！'}
                    </p>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-6 shadow-lg">
                        <p className="text-gray-500 dark:text-gray-400 mb-1">本关得分</p>
                        <p className="text-5xl font-bold text-green-600">{score}</p>
                        <p className="text-sm text-gray-500 mt-2">剩余时间: {formatTime(timeLeft)}</p>
                    </div>

                    <button
                        onClick={currentLevel < levels.length - 1 ? nextLevel : () => { setGameState('menu'); setTotalScore(0); setCurrentLevel(0); }}
                        className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                    >
                        {currentLevel < levels.length - 1 ? '下一关' : '再玩一次'}
                    </button>

                    <Link
                        to="/"
                        className="inline-flex items-center justify-center gap-2 mt-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        返回首页
                    </Link>
                </div>
            </div>
        );
    }

    // 失败界面
    if (gameState === 'lost') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 dark:from-gray-900 dark:to-red-900 py-8 px-4">
                <SEO title="时间到！" description="时间耗尽，再试一次吧！" />
                <div className="max-w-lg mx-auto text-center">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-400 to-orange-500 rounded-full shadow-lg mb-6">
                        <Clock className="w-12 h-12 text-white" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-2">时间到！</h1>
                    <p className="text-gray-600 dark:text-gray-300 mb-6">差一点点就成功了</p>
                    
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-6 shadow-lg">
                        <p className="text-gray-500 dark:text-gray-400 mb-1">本关得分</p>
                        <p className="text-5xl font-bold text-orange-600">{score}</p>
                        <p className="text-sm text-gray-500 mt-2">
                            还剩 {differences.filter(d => !d.found).length} 个不同点
                        </p>
                    </div>

                    <button
                        onClick={startGame}
                        className="w-full py-4 bg-gradient-to-r from-red-500 to-orange-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                    >
                        再试一次
                    </button>

                    <Link
                        to="/"
                        className="inline-flex items-center justify-center gap-2 mt-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        返回首页
                    </Link>
                </div>
            </div>
        );
    }

    // 游戏进行中
    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-gray-900 dark:to-purple-900 py-4 px-2">
            <SEO title={`找不同 - ${level.name}`} description={`找出两张图片中的 ${differences.length} 处不同！`} />
            
            {/* 顶部状态栏 */}
            <div className="max-w-4xl mx-auto mb-4">
                <div className="flex items-center justify-between gap-4">
                    <button
                        onClick={() => setGameState('menu')}
                        className="p-2 bg-white/80 dark:bg-gray-800/80 rounded-lg shadow hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                    
                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">关卡</p>
                            <p className="font-bold text-gray-800 dark:text-white">{currentLevel + 1}/{levels.length}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">得分</p>
                            <p className="font-bold text-blue-600">{score}</p>
                        </div>
                        <div className={`text-center px-3 py-1 rounded-lg ${timeLeft <= 30 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-white/80 dark:bg-gray-800/80'}`}>
                            <p className="text-xs text-gray-500 dark:text-gray-400">时间</p>
                            <p className={`font-bold ${timeLeft <= 30 ? 'text-red-600' : 'text-gray-800 dark:text-white'}`}>
                                {formatTime(timeLeft)}
                            </p>
                        </div>
                        <button
                            onClick={useHint}
                            disabled={hintUsed || differences.every(d => d.found)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium ${
                                hintUsed 
                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed' 
                                    : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                            }`}
                        >
                            💡 提示
                        </button>
                    </div>
                </div>
                
                <div className="mt-2 text-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">{level.name}</span>
                    <span className="text-sm text-gray-400 dark:text-gray-500 ml-2">
                        已找到: {differences.filter(d => d.found).length}/{differences.length}
                    </span>
                </div>
            </div>

            {/* 游戏区域 */}
            <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 原始图片 */}
                    <div 
                        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden cursor-crosshair aspect-square"
                        onClick={handleImageClick}
                    >
                        {renderScene(false)}
                        
                        {/* 已找到的标记 */}
                        {differences.filter(d => d.found).map(d => (
                            <div
                                key={d.id}
                                className="absolute w-6 h-6 bg-green-500 rounded-full flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 animate-ping"
                                style={{ left: `${d.x}%`, top: `${d.y}%` }}
                            >
                                <Check className="w-4 h-4 text-white" />
                            </div>
                        ))}
                        
                        {/* 点击反馈 */}
                        {clickFeedback && (
                            <div
                                className={`absolute w-8 h-8 rounded-full flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 ${
                                    clickFeedback.correct ? 'bg-green-500' : 'bg-red-500'
                                } animate-bounce`}
                                style={{ left: `${clickFeedback.x}%`, top: `${clickFeedback.y}%` }}
                            >
                                {clickFeedback.correct ? (
                                    <Check className="w-5 h-5 text-white" />
                                ) : (
                                    <X className="w-5 h-5 text-white" />
                                )}
                            </div>
                        )}
                        
                        <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                            <Eye className="w-3 h-3 inline mr-1" />
                            原图
                        </div>
                    </div>

                    {/* 修改后的图片 */}
                    <div 
                        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden cursor-crosshair aspect-square"
                        onClick={handleImageClick}
                    >
                        {renderScene(true)}
                        
                        {/* 已找到的标记 */}
                        {differences.filter(d => d.found).map(d => (
                            <div
                                key={d.id}
                                className="absolute w-6 h-6 bg-green-500 rounded-full flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 animate-ping"
                                style={{ left: `${d.x}%`, top: `${d.y}%` }}
                            >
                                <Check className="w-4 h-4 text-white" />
                            </div>
                        ))}
                        
                        {/* 点击反馈 */}
                        {clickFeedback && (
                            <div
                                className={`absolute w-8 h-8 rounded-full flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 ${
                                    clickFeedback.correct ? 'bg-green-500' : 'bg-red-500'
                                } animate-bounce`}
                                style={{ left: `${clickFeedback.x}%`, top: `${clickFeedback.y}%` }}
                            >
                                {clickFeedback.correct ? (
                                    <Check className="w-5 h-5 text-white" />
                                ) : (
                                    <X className="w-5 h-5 text-white" />
                                )}
                            </div>
                        )}
                        
                        <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                            <Sparkles className="w-3 h-3 inline mr-1" />
                            不同
                        </div>
                    </div>
                </div>

                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
                    点击找出两图中的不同之处，共 {differences.length} 处
                </p>
            </div>
        </div>
    );
};

export default Game;
